import { useState, useEffect } from 'react';
import { Modal, Button, Spinner } from '../ui';
import { generateShareGraphic, downloadShareGraphic, shareGraphic } from '../../lib/shareGraphic';

/**
 * ShareGraphicModal - Generate and share Instagram story graphics
 *
 * Generates a 1080x1920 image optimized for Instagram stories
 * with competition details, photo, and Raflr branding.
 */
export default function ShareGraphicModal({ isOpen, onClose, raffle }) {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);

  // Generate image when modal opens
  useEffect(() => {
    if (isOpen && raffle) {
      setLoading(true);
      setError(null);

      generateShareGraphic(raffle)
        .then((url) => {
          setImageUrl(url);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Failed to generate graphic:', err);
          setError('Failed to generate image. Please try again.');
          setLoading(false);
        });
    }
  }, [isOpen, raffle]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setImageUrl(null);
      setLoading(true);
      setError(null);
      setCopied(false);
    }
  }, [isOpen]);

  const handleDownload = () => {
    if (imageUrl) {
      const filename = `raflr-${raffle.title.toLowerCase().replace(/[^a-z0-9]/g, '-').slice(0, 30)}.png`;
      downloadShareGraphic(imageUrl, filename);
    }
  };

  const handleShare = async () => {
    if (imageUrl) {
      setSharing(true);
      await shareGraphic(imageUrl, `Win ${raffle.title} on Raflr!`);
      setSharing(false);
    }
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(`https://raflr.ie/competition/${raffle?.id}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const canNativeShare = typeof navigator !== 'undefined' && navigator.share && navigator.canShare;
  const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Share Your Competition" size="lg" dark>
      <div className="space-y-5">
        {/* Preview */}
        <div className="relative bg-midnight rounded-xl border border-midnight-lighter overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-[360px] sm:h-[420px]">
              <div className="text-center">
                <Spinner size="lg" />
                <p className="mt-4 text-sm text-cream/50">Generating your story...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-[360px] sm:h-[420px]">
              <div className="text-center px-6">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-error/10 flex items-center justify-center">
                  <svg className="w-7 h-7 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <p className="text-error text-sm mb-4">{error}</p>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setLoading(true);
                    setError(null);
                    generateShareGraphic(raffle)
                      .then(setImageUrl)
                      .catch(() => setError('Failed to generate image.'))
                      .finally(() => setLoading(false));
                  }}
                >
                  Try Again
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center p-4 sm:p-6">
              <img
                src={imageUrl}
                alt="Share graphic preview"
                className="max-h-[320px] sm:max-h-[400px] w-auto rounded-lg shadow-xl ring-1 ring-white/10"
                style={{ aspectRatio: '9/16' }}
              />
              {isIOS && (
                <p className="mt-3 text-xs text-cream/40 text-center">
                  Tip: Long-press image → "Add to Photos"
                </p>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {canNativeShare ? (
          <Button
            onClick={handleShare}
            disabled={loading || !!error}
            loading={sharing}
            fullWidth
          >
            <span className="inline-flex items-center">
              <svg className="w-5 h-5 mr-2 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              Save to Photos
            </span>
          </Button>
        ) : (
          <Button
            onClick={handleDownload}
            disabled={loading || !!error}
            fullWidth
          >
            <span className="inline-flex items-center">
              <svg className="w-5 h-5 mr-2 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download
            </span>
          </Button>
        )}

        {/* Competition URL */}
        <div className="space-y-2">
          <label className="block text-xs font-medium text-cream/50 uppercase tracking-wider">
            Competition Link
          </label>
          <div className="flex items-center gap-2 bg-midnight-light border border-midnight-lighter rounded-lg p-2.5">
            <input
              type="text"
              readOnly
              value={`raflr.ie/competition/${raffle?.id}`}
              className="flex-1 bg-transparent text-cream text-sm font-mono outline-none truncate"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCopyUrl}
              className="shrink-0"
            >
              {copied ? (
                <svg className="w-4 h-4 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </Button>
          </div>
        </div>

        {/* Instructions - Collapsible/Compact */}
        <details className="group">
          <summary className="flex items-center gap-2 cursor-pointer text-sm text-cream/60 hover:text-cream/80 transition-colors">
            <svg className="w-4 h-4 transition-transform group-open:rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            How to post on Instagram
          </summary>
          <ol className="mt-3 ml-6 text-sm text-cream/50 space-y-1.5 list-decimal list-outside">
            <li>Download the image above</li>
            <li>Open Instagram and create a new Story</li>
            <li>Select the downloaded image from your gallery</li>
            <li>Add a link sticker with your competition URL</li>
          </ol>
        </details>
      </div>
    </Modal>
  );
}
